import { Injectable, inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { of } from 'rxjs';
import { catchError, map, switchMap, withLatestFrom, filter } from 'rxjs/operators';
import { SeasonApi } from '../../services/season.service';
import { SeasonActions } from '../actions/season.actions';
import { selectSeasonInfo } from '../selectors/season.selectors';
import { ClaimActions } from '../../../reward/store/actions/claim.actions';
import { Store } from '@ngrx/store';
import { selectCurrentPlayerId } from '../../../profile/store/selectors/profileGeneral.selector';

import { ProfileGeneralActions } from '../../../profile/store/actions/profileGeneral.action';

@Injectable()
export class SeasonEffects {
  private actions$ = inject(Actions);
  private seasonApi = inject(SeasonApi);
  private store = inject(Store);

  onClaimSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        ClaimActions.claimRewardSuccess,
        SeasonActions.claimChallengeSuccess,
        SeasonActions.claimSeasonRewardSuccess
      ),
      switchMap(() => [
        SeasonActions.loadSeasonChallenges(),
        SeasonActions.loadUnclaimedRewards()
      ])
    )
  );

  refreshProgressOnClaim$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SeasonActions.claimChallengeSuccess, SeasonActions.claimSeasonRewardSuccess),
      withLatestFrom(
        this.store.select(selectSeasonInfo),
        this.store.select(selectCurrentPlayerId),
        this.store.select(state => state.season.lastFromNumber),
        this.store.select(state => state.season.lastToNumber)
      ),
      filter(([action, season, profileId]) => !!season?.id && !!profileId),
      switchMap(([action, season, profileId, fromNumber, toNumber]) => [
        SeasonActions.loadSeasonProgress({
          seasonId: season!.id,
          profileId: profileId!
        }),
        SeasonActions.loadSeasonLevelsProgress({
          seasonId: season!.id,
          profileId: profileId!,
          fromNumber: fromNumber,
          toNumber: toNumber
        })
      ])
    )
  );

  loadActiveSeasons$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SeasonActions.loadActiveSeasons),
      switchMap(() =>
        this.seasonApi.fetchActiveSeasons().pipe(
          map((response) => SeasonActions.loadActiveSeasonsSuccess({ response })),
          catchError((error) => of(SeasonActions.loadActiveSeasonsFailure({ error })))
        )
      )
    )
  );

  loadSeasonInfo$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SeasonActions.loadSeasonInfo),
      switchMap(({ seasonId }) =>
        this.seasonApi.fetchSeasonInfo(seasonId).pipe(
          map((season) => SeasonActions.loadSeasonInfoSuccess({ season })),
          catchError((error) => of(SeasonActions.loadSeasonInfoFailure({ error })))
        )
      )
    )
  );

  onSeasonDetailsOpened$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SeasonActions.openSeasonDetails),
      withLatestFrom(this.store.select(selectSeasonInfo), this.store.select(selectCurrentPlayerId)),
      switchMap(([{ seasonId }, currentSeason, profileId]) => {
        const actions: any[] = [
          SeasonActions.loadSeasonLevels({ seasonId })
        ];

        if (!currentSeason || currentSeason.id !== seasonId) {
          actions.push(SeasonActions.loadSeasonInfo({ seasonId }));
        } else if (profileId) {
          actions.push(SeasonActions.loadSeasonProgress({ seasonId, profileId }));
        }

        return actions;
      })
    )
  );

  loadSeasonProgress$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SeasonActions.loadSeasonProgress),
      switchMap(({ seasonId, profileId }) =>
        this.seasonApi.fetchSeasonProgress(seasonId, profileId).pipe(
          map((response) => SeasonActions.loadSeasonProgressSuccess({ response })),
          catchError((error) => of(SeasonActions.loadSeasonProgressFailure({ error })))
        )
      )
    )
  );

  triggerProgressOnSeasonLoaded$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SeasonActions.loadSeasonInfoSuccess),
      withLatestFrom(this.store.select(selectCurrentPlayerId)),
      filter(([action, profileId]) => !!profileId && !!action.season.id),
      map(([action, profileId]) => SeasonActions.loadSeasonProgress({
        seasonId: action.season.id,
        profileId: profileId!
      }))
    )
  );

  triggerProgressOnProfileLoaded$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ProfileGeneralActions.fetchCurrentPlayerDataSuccess),
      withLatestFrom(this.store.select(selectSeasonInfo)),
      filter(([action, season]) => !!season && !!season.id),
      map(([action, season]) => SeasonActions.loadSeasonProgress({
        seasonId: season!.id,
        profileId: action.payload.playerId
      }))
    )
  );

  loadSeasonChallenges$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SeasonActions.loadSeasonChallenges),
      switchMap(() =>
        this.seasonApi.fetchSeasonChallenges().pipe(
          map((response) => SeasonActions.loadSeasonChallengesSuccess({ response })),
          catchError((error) => of(SeasonActions.loadSeasonChallengesFailure({ error })))
        )
      )
    )
  );

  loadSeasonLevels$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SeasonActions.loadSeasonLevels),
      switchMap(({ seasonId }) =>
        this.seasonApi.fetchSeasonLevels(seasonId).pipe(
          map((response) => SeasonActions.loadSeasonLevelsSuccess({ response })),
          catchError((error) => of(SeasonActions.loadSeasonLevelsFailure({ error })))
        )
      )
    )
  );

  loadSeasonLevelsProgress$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SeasonActions.loadSeasonLevelsProgress),
      switchMap(({ seasonId, profileId, fromNumber, toNumber }) =>
        this.seasonApi.fetchSeasonLevelsProgress(seasonId, profileId, fromNumber, toNumber).pipe(
          map((response) => SeasonActions.loadSeasonLevelsProgressSuccess({ response })),
          catchError((error) => of(SeasonActions.loadSeasonLevelsProgressFailure({ error })))
        )
      )
    )
  );

  loadUnclaimedRewards$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SeasonActions.loadUnclaimedRewards),
      switchMap(() =>
        this.seasonApi.fetchUnclaimedRewards().pipe(
          map((response) => SeasonActions.loadUnclaimedRewardsSuccess({ response })),
          catchError((error) => of(SeasonActions.loadUnclaimedRewardsFailure({ error })))
        )
      )
    )
  );

  claimChallenge$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SeasonActions.claimChallenge),
      switchMap(({ challengeId }) =>
        this.seasonApi.claimChallenge(challengeId).pipe(
          map(() => SeasonActions.claimChallengeSuccess({ challengeId })),
          catchError((error) => of(SeasonActions.claimChallengeFailure({ error })))
        )
      )
    )
  );

  claimSeasonReward$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SeasonActions.claimSeasonReward),
      switchMap(({ rewardIds, profileId }) =>
        this.seasonApi.claimSeasonReward(rewardIds, profileId).pipe(
          map(() => SeasonActions.claimSeasonRewardSuccess({ rewardIds })),
          catchError((error) => of(SeasonActions.claimSeasonRewardFailure({ error })))
        )
      )
    )
  );

  claimAllUnclaimedRewards$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SeasonActions.claimAllUnclaimedRewards),
      withLatestFrom(this.store.select(selectSeasonInfo)),
      filter(([action, season]) => !!season?.id),
      switchMap(([action, season]) =>
        this.seasonApi.claimAllUnclaimedRewards(season!.id).pipe(
          switchMap((rewards) => [
            SeasonActions.claimAllUnclaimedRewardsSuccess({ rewards }),
            SeasonActions.loadSeasonChallenges(),
            SeasonActions.loadUnclaimedRewards()
          ]),
          catchError((error) => of(SeasonActions.claimAllUnclaimedRewardsFailure({ error })))
        )
      )
    )
  );

  refreshProgressOnClaimAll$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SeasonActions.claimAllUnclaimedRewardsSuccess),
      withLatestFrom(
        this.store.select(selectSeasonInfo),
        this.store.select(selectCurrentPlayerId),
        this.store.select(state => state.season.lastFromNumber),
        this.store.select(state => state.season.lastToNumber)
      ),
      filter(([action, season, profileId]) => !!season?.id && !!profileId),
      switchMap(([action, season, profileId, fromNumber, toNumber]) => [
        SeasonActions.loadSeasonProgress({
          seasonId: season!.id,
          profileId: profileId!
        }),
        SeasonActions.loadSeasonLevelsProgress({
          seasonId: season!.id,
          profileId: profileId!,
          fromNumber: fromNumber,
          toNumber: toNumber
        })
      ])
    )
  );

  loadSeasons$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SeasonActions.loadSeasons),
      switchMap(() =>
        this.seasonApi.fetchActiveSeasons().pipe(
          map((response) => SeasonActions.loadSeasonsSuccess({ response })),
          catchError((error) => of(SeasonActions.loadSeasonsFailure({ error })))
        )
      )
    )
  );
}
